import { ThemeOptions } from '@mui/material';
import type {} from '@mui/x-data-grid-pro/themeAugmentation';
import type {} from '@mui/x-date-pickers/themeAugmentation';
import type {} from '@mui/x-date-pickers-pro/themeAugmentation';

export const offWhite = '#faf9f6';

/**
 * Application specific overrides to the default MUI Theme Options.
 * Company and Brand level overrides will take precedence over Application level override.
 */
export const AppThemeOverrides: ThemeOptions = {
	breakpoints: {
		values: {
			xs: 501,
			sm: 601,
			md: 1001,
			lg: 1200,
			xl: 1725
		}
	},
	palette: {
		common: {
			white: offWhite
		},
		background: {
			default: offWhite,
			paper: offWhite
		},

		priority1: {
			main: '255, 255, 255'
		},
		priority2: {
			main: '230, 133, 133'
		},
		priority3: {
			main: '218, 187, 32'
		},
		priority4: {
			main: '153, 194, 149'
		},
		priority5: {
			main: '232, 179, 151'
		}
	},
	components: {
		MuiAlert: {
			defaultProps: {
				variant: 'filled'
			}
		},
		MuiCollapse: {
			defaultProps: {
				style: {
					minHeight: 'auto'
				}
			}
		},
		MuiSelect: {
			defaultProps: {
				size: 'small',
				notched: true
			}
		},
		MuiInputLabel: {
			defaultProps: {
				shrink: true
			}
		},
		MuiButton: {
			defaultProps: {
				style: {
					height: 32
				}
			}
		},
		MuiOutlinedInput: {
			defaultProps: {
				notched: true,
				sx: {
					height: 32
				},
				style: {
					paddingRight: '2px'
				}
			}
		},
		MuiDrawer: {
			defaultProps: {
				transitionDuration: 300
			}
		},
		MuiDateTimePicker: {
			defaultProps: {
				ampm: false,
				format: 'MM/DD/YY HH:mm',
				closeOnSelect: true,
				slotProps: {
					field: {
						openPickerButtonPosition: 'start'
					},
					textField: {
						style: {
							height: '32px',
							paddingRight: '2px'
						},
						sx: {
							'& .MuiPickersInputBase-root': {
								height: 32
							},
							sx: {
								'&.MuiIconButton-root': {
									height: 12,
									padding: 4
								}
							}
						}
					},
					dialog: {
						sx: {
							zIndex: 12050
						}
					}
				}
			}
		},
		MuiDatePicker: {
			defaultProps: {
				format: 'MM/DD/YY',
				closeOnSelect: true,
				slotProps: {
					field: {
						openPickerButtonPosition: 'start'
					},
					textField: {
						style: {
							height: '32px',
							paddingRight: '2px'
						},
						sx: {
							'& .MuiPickersInputBase-root': {
								height: 32
							},
							sx: {
								'&.MuiIconButton-root': {
									height: 12,
									padding: 4
								}
							}
						}
					},
					dialog: {
						sx: {
							zIndex: 12050
						}
					}
				}
			}
		},
		MuiTimePicker: {
			defaultProps: {
				ampm: false,
				closeOnSelect: true,
				format: 'HH:mm',
				slotProps: {
					field: {
						openPickerButtonPosition: 'start'
					},
					textField: {
						style: {
							height: '32px',
							paddingRight: '2px'
						},
						sx: {
							'& .MuiPickersInputBase-root': {
								height: 32
							},
							sx: {
								'&.MuiIconButton-root': {
									height: 12,
									padding: 4
								}
							}
						}
					},
					dialog: {
						sx: {
							zIndex: 12050
						}
					}
				}
			}
		},
		MuiDateRangePicker: {
			defaultProps: {
				format: 'MM/DD/YY',
				closeOnSelect: true,
				slotProps: {
					field: {
						openPickerButtonPosition: 'start'
					},
					textField: {
						style: {
							height: '32px',
							paddingRight: '2px'
						},
						sx: {
							'& .MuiPickersInputBase-root': {
								height: 32
							},
							sx: {
								'&.MuiIconButton-root': {
									height: 12,
									padding: 4
								}
							}
						}
					},
					dialog: {
						sx: {
							zIndex: 12050
						}
					}
				}
			}
		},
		MuiTextField: {
			defaultProps: {
				sx: {}
			}
		},
		MuiFormHelperText: {
			defaultProps: {
				sx: {
					fontSize: '10px'
				}
			}
		},
		MuiCheckbox: {
			defaultProps: {
				sx: {
					padding: '2px 4px'
				}
			}
		},
		MuiDataGrid: {
			defaultProps: {
				disableColumnMenu: true,
				disableColumnResize: true,
				disableColumnFilter: true,
				hideFooter: true,
				showToolbar: true,
				sx: {
					'& .MuiDataGrid-cell:focus': {
						outline: 'none'
					},
					'& .MuiDataGrid-cell:focus-within': {
						outline: 'none'
					},
					'& .MuiDataGrid-columnHeader:focus-within': {
						outline: 'none'
					},
					'& .MuiDataGrid-row.MuiDataGrid-row--editable.MuiDataGrid-row--editing .MuiDataGrid-cell:focus': {
						outline: 'none'
					},
					'& .MuiDataGrid-row.MuiDataGrid-row--editable.MuiDataGrid-row--editing .MuiDataGrid-cell:focus-within': {
						outline: 'none'
					}
				}
			}
		},
		MuiSnackbarContent: {
			styleOverrides: {
				root: {
					backgroundColor: 'rgb(250, 249, 246)',
					color: 'rgba(0, 0, 0, 0.87)'
				}
			}
		}
	}
};

export function applyThemeGridSxOverrides() {
	const gridDefaultStyles = AppThemeOverrides.components?.MuiDataGrid?.defaultProps?.sx as Record<string, Record<string, string>>;
	if (typeof gridDefaultStyles === 'object') {
		return { ...gridDefaultStyles };
	}
}
